import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Building2, Target, Snowflake, Filter, Phone, MessageCircle, History, MessageSquarePlus, Check, RotateCcw } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { normalize as _nz } from "../lib/search";

const TYPE_META = {
  client: { label: "Cliente", icon: Building2, color: "bg-emerald-100 text-emerald-700" },
  prospect: { label: "Prospecto", icon: Target, color: "bg-blue-100 text-blue-700" },
  cold: { label: "Frío", icon: Snowflake, color: "bg-sky-100 text-sky-700" },
};
const STAGE_COLOR = { "Frío": "bg-sky-100 text-sky-700", "Nuevo": "bg-blue-100 text-blue-700", "Contactado": "bg-cyan-100 text-cyan-700", "Calificado": "bg-indigo-100 text-indigo-700", "Cotización": "bg-amber-100 text-amber-700", "Negociación": "bg-orange-100 text-orange-700", "Ganado": "bg-emerald-100 text-emerald-700", "Perdido": "bg-slate-200 text-slate-600", "Cliente": "bg-emerald-100 text-emerald-700" };
const FUP_TYPES = ["Llamada", "WhatsApp", "Correo", "Visita", "Cotización", "Cobro", "Reunión", "Otro"];
const PRIORITIES = ["Alta", "Media", "Baja"];
const today = () => new Date(Date.now()-216e5).toISOString().slice(0, 10);
const backendType = (t) => (t === "cold" ? "prospect" : t);

export default function Pipeline() {
  const { user } = useAuth();
  const [data, setData] = useState({ rows: [], is_manager: false, sellers: [], stages: [] });
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const [stage, setStage] = useState("");
  const [vendor, setVendor] = useState("");
  const [q, setQ] = useState("");
  // acciones
  const [segTarget, setSegTarget] = useState(null);
  const [segForm, setSegForm] = useState({ type: "Llamada", motivo: "", note: "", date: today(), priority: "Media" });
  const [contactT, setContactT] = useState(null);
  const [contactPhone, setContactPhone] = useState("");
  const [ficha, setFicha] = useState(null);

  const fetchData = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (type) params.set("type", type);
    if (stage) params.set("stage", stage);
    if (vendor) params.set("vendor_id", vendor);
    api.get(`/pipeline?${params.toString()}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [type, stage, vendor]);

  const rows = useMemo(() => {
    const t = _nz(q.trim());
    return t ? data.rows.filter((r) => _nz(r.name).includes(t)) : data.rows;
  }, [data.rows, q]);

  const fmt = (cur, n) => `${cur === "USD" ? "$" : "Q"}${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const kpis = useMemo(() => {
    let totalG = 0, totalU = 0, negG = 0, negU = 0, risk = 0;
    rows.forEach((r) => {
      const v = r.value || 0;
      if (r.currency === "USD") { totalU += v; if (r.stage === "Negociación") negU += v; }
      else { totalG += v; if (r.stage === "Negociación") negG += v; }
      if (r.days_inactive != null && r.days_inactive > 30) risk += 1;
    });
    return { totalG, totalU, negG, negU, risk, count: rows.length };
  }, [rows]);

  const openSeg = (r, react = false) => { setSegForm({ type: "Llamada", motivo: react ? "Reactivación de contacto" : "", note: "", date: today(), priority: react ? "Alta" : "Media" }); setSegTarget(r); };
  const saveSeg = async () => {
    if (!segForm.motivo) return toast.error("Indica el motivo");
    try {
      await api.post("/followups", { kind: "seguimiento", parent_type: backendType(segTarget.type), parent_id: segTarget.id, type: segForm.type, motivo: segForm.motivo, note: segForm.note, date: segForm.date, priority: segForm.priority });
      toast.success("Seguimiento registrado"); setSegTarget(null); fetchData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openContact = (r) => { setContactPhone(r.phone || ""); setContactT(r); };
  const openFicha = async (r) => {
    setFicha({ name: r.name, entries: null });
    try { const res = await api.get(`/tracking/timeline?parent_type=${backendType(r.type)}&parent_id=${r.id}`); setFicha({ name: r.name, ...res.data }); }
    catch { setFicha({ name: r.name, entries: [], count: 0 }); }
  };

  return (
    <div className="space-y-5" data-testid="pipeline-page">
      <div>
        <h1 className="text-2xl font-heading font-bold">Pipeline comercial</h1>
        <p className="text-sm text-muted-foreground">Estado y línea de seguimiento de todos tus clientes, prospectos y clientes fríos{data.is_manager ? " del equipo" : ""}.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pipeline-kpis">
        <div className="rounded-sm border p-3 bg-muted/30"><p className="text-2xl font-heading font-bold leading-none">{kpis.count}</p><p className="text-[11px] uppercase tracking-wider mt-1 text-muted-foreground">Registros</p></div>
        <div className="rounded-sm border p-3 border-primary/30 bg-primary/10 text-primary"><p className="text-lg font-heading font-bold leading-none">{fmt("GTQ", kpis.totalG)}{kpis.totalU > 0 ? ` · ${fmt("USD", kpis.totalU)}` : ""}</p><p className="text-[11px] uppercase tracking-wider mt-1 opacity-80">Valor en pipeline</p></div>
        <div className="rounded-sm border p-3 border-orange-500/30 bg-orange-500/10 text-orange-700"><p className="text-lg font-heading font-bold leading-none">{fmt("GTQ", kpis.negG)}{kpis.negU > 0 ? ` · ${fmt("USD", kpis.negU)}` : ""}</p><p className="text-[11px] uppercase tracking-wider mt-1 opacity-80">En negociación</p></div>
        <div className="rounded-sm border p-3 border-red-500/30 bg-red-500/10 text-red-700"><p className="text-2xl font-heading font-bold leading-none">{kpis.risk}</p><p className="text-[11px] uppercase tracking-wider mt-1 opacity-80">En riesgo (+30 días)</p></div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-1.5 text-muted-foreground text-xs"><Filter className="h-3.5 w-3.5" /> Filtros:</div>
        <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-sm h-9 w-[150px]" data-testid="pipeline-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos los tipos</SelectItem><SelectItem value="client">Clientes</SelectItem><SelectItem value="prospect">Prospectos</SelectItem><SelectItem value="cold">Clientes fríos</SelectItem></SelectContent>
        </Select>
        <Select value={stage || "all"} onValueChange={(v) => setStage(v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-sm h-9 w-[160px]" data-testid="pipeline-stage"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos los estados</SelectItem>{(data.stages || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        {data.is_manager && (
          <Select value={vendor || "all"} onValueChange={(v) => setVendor(v === "all" ? "" : v)}>
            <SelectTrigger className="rounded-sm h-9 w-[180px]" data-testid="pipeline-vendor"><SelectValue placeholder="Vendedor" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todos los vendedores</SelectItem>{(data.sellers || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre…" className="rounded-sm h-9 w-[200px]" data-testid="pipeline-search" />
        <Badge className="rounded-sm bg-muted text-foreground ml-auto">{rows.length} registros</Badge>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
        <Card className="rounded-sm border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pipeline-table">
              <thead className="bg-muted/50 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left font-bold px-3 py-2.5">Nombre</th>
                  <th className="text-left font-bold px-3 py-2.5">Tipo</th>
                  <th className="text-left font-bold px-3 py-2.5">Estado</th>
                  <th className="text-left font-bold px-3 py-2.5">Último seguimiento</th>
                  <th className="text-left font-bold px-3 py-2.5">Próxima acción</th>
                  <th className="text-left font-bold px-3 py-2.5">Valor / Prob.</th>
                  <th className="text-left font-bold px-3 py-2.5">Responsable</th>
                  <th className="text-right font-bold px-3 py-2.5">Días sin contacto</th>
                  <th className="text-right font-bold px-3 py-2.5">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? <tr><td colSpan={9} className="text-center text-muted-foreground py-10">Sin registros.</td></tr> :
                  rows.map((r) => {
                    const M = TYPE_META[r.type] || TYPE_META.prospect; const Icon = M.icon;
                    const di = r.days_inactive;
                    const diColor = di == null ? "text-muted-foreground" : di > 30 ? "text-red-600 font-bold" : di > 15 ? "text-amber-600 font-semibold" : "text-emerald-600";
                    const riskCls = di == null ? "" : di > 30 ? "bg-red-500/[0.06]" : di > 15 ? "bg-amber-500/[0.06]" : "";
                    return (
                      <tr key={`${r.type}-${r.id}`} className={`border-t border-border hover:bg-muted/30 ${riskCls}`} data-testid={`pipeline-row-${r.id}`}>
                        <td className="px-3 py-2.5 font-semibold">{r.name}</td>
                        <td className="px-3 py-2.5"><Badge className={`rounded-sm text-[10px] gap-1 ${M.color}`}><Icon className="h-3 w-3" /> {M.label}</Badge></td>
                        <td className="px-3 py-2.5"><Badge className={`rounded-sm text-[10px] ${STAGE_COLOR[r.stage] || "bg-muted"}`}>{r.stage}</Badge></td>
                        <td className="px-3 py-2.5 text-xs">{r.last_date ? <><span className="font-mono">{r.last_date}</span>{r.last_result ? <span className="text-muted-foreground"> · {r.last_result}</span> : ""}</> : <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-3 py-2.5 text-xs">{r.next_date ? <><span className="font-mono">{r.next_date}</span>{r.next_action ? <span className="text-muted-foreground"> · {r.next_action}</span> : ""}</> : <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-3 py-2.5 text-xs">{r.value > 0 ? <span className="font-mono">{fmt(r.currency, r.value)}</span> : <span className="text-muted-foreground">—</span>}{r.probability ? <span className="text-muted-foreground"> · {r.probability}%</span> : ""}</td>
                        <td className="px-3 py-2.5 text-xs text-muted-foreground">{r.vendor_name || "—"}</td>
                        <td className={`px-3 py-2.5 text-right text-xs ${diColor}`}>{di == null ? "—" : `${di} d`}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-end gap-1">
                            {di != null && di > 30 && <Button size="icon" variant="outline" className="rounded-sm h-7 w-7 text-red-600 border-red-500/40" title="Reactivar" onClick={() => openSeg(r, true)} data-testid={`pipe-react-${r.id}`}><RotateCcw className="h-3.5 w-3.5" /></Button>}
                            <Button size="icon" variant="outline" className="rounded-sm h-7 w-7" title="Registrar seguimiento" onClick={() => openSeg(r)} data-testid={`pipe-seg-${r.id}`}><MessageSquarePlus className="h-3.5 w-3.5" /></Button>
                            <Button size="icon" variant="outline" className="rounded-sm h-7 w-7" title="Llamar / WhatsApp" onClick={() => openContact(r)} data-testid={`pipe-contact-${r.id}`}><Phone className="h-3.5 w-3.5" /></Button>
                            <Button size="icon" variant="outline" className="rounded-sm h-7 w-7" title="Ver ficha 360°" onClick={() => openFicha(r)} data-testid={`pipe-ficha-${r.id}`}><History className="h-3.5 w-3.5" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Registrar seguimiento */}
      <Dialog open={!!segTarget} onOpenChange={(o) => !o && setSegTarget(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="pipe-seg-dialog">
          <DialogHeader><DialogTitle className="font-heading">Registrar seguimiento</DialogTitle>
            <DialogDescription>{segTarget?.name}</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Tipo</Label>
              <Select value={segForm.type} onValueChange={(v) => setSegForm({ ...segForm, type: v })}><SelectTrigger className="rounded-sm" data-testid="pipe-seg-type"><SelectValue /></SelectTrigger>
                <SelectContent>{FUP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Prioridad</Label>
              <Select value={segForm.priority} onValueChange={(v) => setSegForm({ ...segForm, priority: v })}><SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5 col-span-2"><Label className="text-xs font-bold uppercase tracking-widest">Fecha</Label>
              <Input type="date" className="rounded-sm" value={segForm.date} onChange={(e) => setSegForm({ ...segForm, date: e.target.value })} data-testid="pipe-seg-date" /></div>
            <Input placeholder="Motivo *" className="rounded-sm col-span-2" value={segForm.motivo} onChange={(e) => setSegForm({ ...segForm, motivo: e.target.value })} data-testid="pipe-seg-motivo" />
            <Textarea placeholder="Observaciones" className="rounded-sm col-span-2" value={segForm.note} onChange={(e) => setSegForm({ ...segForm, note: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setSegTarget(null)}>Cancelar</Button>
            <Button className="rounded-sm font-bold" onClick={saveSeg} data-testid="pipe-seg-save"><Check className="h-4 w-4 mr-1" /> Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contactar */}
      <Dialog open={!!contactT} onOpenChange={(o) => !o && setContactT(null)}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="pipe-contact-dialog">
          <DialogHeader><DialogTitle className="font-heading">Contactar · {contactT?.name}</DialogTitle>
            <DialogDescription>Verifica o edita el número antes de contactar.</DialogDescription></DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-widest">Teléfono</Label>
            <Input className="rounded-sm" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="Número de teléfono" data-testid="pipe-contact-phone" />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" className="rounded-sm gap-1" disabled={!contactPhone} onClick={() => window.open(`tel:${contactPhone.replace(/[^0-9+]/g, "")}`)} data-testid="pipe-contact-call"><Phone className="h-4 w-4" /> Llamar</Button>
            <Button className="rounded-sm gap-1 bg-emerald-600 hover:bg-emerald-700" disabled={!contactPhone} onClick={() => { const n = contactPhone.replace(/[^0-9]/g, ""); const full = n.length <= 8 ? `502${n}` : n; window.open(`https://wa.me/${full}`, "_blank"); }} data-testid="pipe-contact-wa"><MessageCircle className="h-4 w-4" /> WhatsApp</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ficha 360° (historial) */}
      <Dialog open={!!ficha} onOpenChange={(o) => !o && setFicha(null)}>
        <DialogContent className="rounded-sm max-w-lg max-h-[85vh] overflow-y-auto" data-testid="pipe-ficha-dialog">
          <DialogHeader><DialogTitle className="font-heading">Ficha 360° · {ficha?.name}</DialogTitle>
            <DialogDescription>{ficha?.entries == null ? "Cargando…" : `${ficha?.count || 0} gestión(es) registradas.`}</DialogDescription></DialogHeader>
          <div className="space-y-2">
            {ficha?.entries == null ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> :
              (ficha.entries.length === 0 ? <p className="text-sm text-muted-foreground">Sin gestiones registradas todavía.</p> :
                ficha.entries.map((e, i) => (
                  <div key={i} className="flex gap-3 border-l-2 border-primary/30 pl-3 pb-2">
                    <div className="flex-1">
                      <p className="text-sm font-semibold">{e.action} <span className="text-xs font-normal text-muted-foreground">· {e.type}</span></p>
                      {e.note && <p className="text-xs">{e.note}</p>}
                      <p className="text-[10px] text-muted-foreground">{(e.at || "").slice(0, 16).replace("T", " ")} · {e.by}</p>
                    </div>
                  </div>
                )))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
